import { LS_ACCESSTOKEN, LS_USER } from '@config/localstorage.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import useStorage from '@composables/useStorage.js'
import { isObj, clone } from '@helpers/utils.js'
import { GAME_ID } from '@config/game.js'
import { computed } from 'vue'
import {
  MEMBER_LEVEL_MANAGER,
  MEMBER_LEVEL_MEMBER,
  MEMBER_LEVEL_LEADER,
  STATUS_DELETED,
} from '@config/options.js'
import {
  subscriptionStore,
  myTeamsStore,
  ratingStore,
  karmaStore,
} from '@stores'

const DEFAULT_LANG = 'en'
const accessTokenState = useStorage(LS_ACCESSTOKEN, null, false)
const userState = useStorage(LS_USER, null, false)

export default function useAuth() {
  const { subscriptions, clearSubscriptions } = subscriptionStore()
  const { clearMyTeamsStore } = myTeamsStore()
  const { clearRatings } = ratingStore()
  const { clearKarma } = karmaStore()

  const accessTokens = computed(() => {
    try {
      const json = JSON.parse(accessTokenState.value)
      if (isObj(json)) return json
    } catch (error) {
      console.error(error)
    }
    return {}
  })
  const user = computed(() => JSON.parse(userState.value))

  const isLoggedIn = computed(() =>
    user.value?.id && accessTokens.value[GAME_ID] ? true : false
  )

  function setUser(user) {
    if (!user) {
      throw new Error()
    }
    userState.value = JSON.stringify(user)
    sdkInterface.setUserId(user.id)
  }

  function setToken(accessToken) {
    if (!accessToken) {
      throw new Error()
    }
    accessTokenState.value = JSON.stringify({
      ...accessTokens.value,
      [GAME_ID]: accessToken,
    })
  }

  function clearUser() {
    clearSubscriptions()
    clearMyTeamsStore()
    clearRatings()
    clearKarma()
    userState.value = JSON.stringify({})
    sdkInterface.setAuthToken('')
    const tokens = clone(accessTokens.value)
    delete tokens[GAME_ID]
    accessTokenState.value = JSON.stringify(tokens)
  }

  function getUserLanguage() {
    return user.value?.language || DEFAULT_LANG
  }

  /**
   * @param {'admin' | 'manager' | 'member'} level
   */
  function isSuperAdmin(level) {
    let access

    switch (level) {
      case 'admin':
        access = MEMBER_LEVEL_LEADER
        break

      case 'manager':
        access = MEMBER_LEVEL_MANAGER
        break

      default:
        access = MEMBER_LEVEL_MEMBER
        break
    }

    return user.value?.info?.level >= access
  }

  const isAccountClosed = computed(
    () => user.value?.info?.status === STATUS_DELETED
  )

  return {
    isAccountClosed,
    getUserLanguage,
    subscriptions,
    isSuperAdmin,
    accessTokens,
    isLoggedIn,
    clearUser,
    setToken,
    setUser,
    user,
  }
}
